/**
 * @lines98/client/lib/leaderboard-snapshot — оптимистичный per-player снимок ПОСЛЕДНЕЙ
 * показанной страницы лидерборда для МГНОВЕННОЙ отрисовки строк ДО ресолва сети
 * (instant-ui-hydration §3). Ветка над общим «стволом» `lib/screen-snapshot`.
 *
 * ЗАЧЕМ / WHY THIS EXISTS (instant-hydration на лидерборд+профиль 2026-06-10):
 * у лидерборда СВОЙ источник (страницы борда — `leaderboard-api`, НЕ `/meta/state`), но
 * проблема та же: на входе/перезагрузке строки рисовались только ПОСЛЕ async-fetch
 * («Загружаем лидерборд…» → строки). Этот снимок хранит последнюю показанную страницу
 * КАЖДОЙ вкладки/режима борда (ключ кэша = вкладка/режим), чтобы повторный заход на ту же
 * вкладку рисовал её строки СРАЗУ; затем fetch reconcile перетирает server-truth'ом.
 *
 * **Ключ кэша — ПО ВКЛАДКЕ/РЕЖИМУ (`PaneKey`).** Доска дня (Сегодня/Вчера) и каждый режим
 * рекордов + борд мастер-ювелира — РАЗНЫЕ страницы → раздельные записи внутри снимка. Тип
 * строк тоже разный: score-строки (`RowVm`) vs karat-строки (`KaratRowVm`) — храним
 * дискриминированно (`kind`), чтобы рендер не «фейкал» счёт из карата.
 *
 * **Server-truth/анти-чит цел (read-only показ):** лидерборд — ЧИСТО показ; снимок ничего
 * не «решает». `short_id`-видимость (foundational invariant anti-shadow-practice) приходит
 * с СЕРВЕРА в каждой строке — снимок лишь кэширует то, что СЕРВЕР УЖЕ отдал (включая
 * `null`-короткий-id у teaser-строк). На холодном кэше teaser остаётся teaser; reconcile
 * перетирает server-truth'ом. Снимок не вход для проверок и не импортируется движком.
 *
 * **Изоляция по игроку (privacy + own-row).** Строки несут `isViewer` (своя строка) —
 * её состав зависит от того, КТО смотрит (свой `short_id` раскрыт на live-борде, §6.3).
 * Поэтому снимок keyed по playerId (ствол): аккаунт A не видит снимок B; гость → пусто.
 *
 * Cross-references:
 *   - lib/screen-snapshot.ts (ствол: per-player snapshot-движок — load/save/clear).
 *   - lib/meta-snapshot.ts (параллельная ветка: мета-динамика игрока).
 *   - screens/leaderboard-screen.ts (consumer: seed paneState из снимка → reconcile).
 *   - services/leaderboard-api.ts (источник строк; server-truth — следующий fetch).
 *   - .claude/skills/instant-ui-hydration/SKILL.md §3.
 */

import { createScreenSnapshot } from './screen-snapshot.js';

// =============================================================================
// Types — кэшируемые строки (подмножество того, что рендерит экран)
// =============================================================================

/**
 * Кэшируемая score-строка — ровно поля, что рисует `renderRow` (rank/name/tag/score/moves/
 * own-флаг/short_id). `shortId` несёт server-truth §6.3-видимость verbatim (present →
 * playable; null → teaser). Это НЕ клиентское решение — кэш лишь хранит серверную форму.
 */
export interface CachedRowVm {
  readonly rank: number;
  readonly playerName: string;
  readonly playerTag: string;
  /** Player level (display-only badge). `null` → no badge (mock / defensive). */
  readonly level: number | null;
  readonly scoreVerified: number;
  readonly movesCount: number;
  readonly isViewer: boolean;
  readonly shortId: string | null;
}

/** Кэшируемая karat-строка (борд мастер-ювелира) — другой набор колонок (без счёта/ходов). */
export interface CachedKaratRowVm {
  readonly rank: number;
  readonly playerName: string;
  readonly playerTag: string;
  /** Player level (display-only badge). `null` → no badge. */
  readonly level: number | null;
  readonly karat: number;
  readonly isViewer: boolean;
}

/** Одна кэшированная страница — дискриминирована по типу строк (score vs karat). */
export type CachedPane =
  | { readonly kind: 'score'; readonly rows: readonly CachedRowVm[] }
  | { readonly kind: 'karat'; readonly rows: readonly CachedKaratRowVm[] };

/**
 * Снимок лидерборда: последняя показанная страница ПО КЛЮЧУ вкладки/режима. Ключ — строка
 * `PaneKey` экрана (`daily:today` / `daily:yesterday` / `<mode>` / `master_jeweler`).
 * Храним map, чтобы переключение вкладок не стирало кэш других.
 */
export interface LeaderboardSnapshot {
  /** Кэш-страницы по ключу вкладки/режима борда. */
  readonly panes: Readonly<Record<string, CachedPane>>;
  /** ms epoch снимка (отладка/возможная TTL; рендер от него не зависит). */
  readonly timestamp: number;
}

// =============================================================================
// Constants + общий снимок-«ствол»
// =============================================================================

/** Префикс ключа; полный ключ = `${PREFIX}${playerId}` (изоляция по игроку). */
const KEY_PREFIX = 'lines98.leaderboard.snapshot.v1.';
/**
 * Версия схемы payload (несовпадение → снимок битый, отбрасываем). Bump 1→2 (2026-06-11):
 * добавлено поле `level` в кэш-строки (бейдж уровня у каждого игрока) — старый снимок без
 * `level` отбрасывается (UX-кэш, безвреден: следующий fetch перетирает server-truth'ом).
 */
const SCHEMA_VERSION = 2;

const store = createScreenSnapshot<LeaderboardSnapshot>({
  keyPrefix: KEY_PREFIX,
  version: SCHEMA_VERSION,
  isValid: isLeaderboardSnapshot,
});

// =============================================================================
// Pane-key helpers — ЕДИНЫЙ источник ключа daily-страницы (date-stamped)
// =============================================================================

/**
 * Ключ кэш-страницы доски дня = `daily:<date_of_day>` (ФАКТИЧЕСКАЯ дата), НЕ `daily:today`.
 *
 * **rollover-fix 2026-06-11.** Раньше ключ нёс id под-таба (`daily:today`/`daily:yesterday`),
 * НЕ дату → после смены дня вчерашний снимок (под тем же ключом `daily:today`) подставлялся
 * на новую доску (owner-playtest: лидерборд «Сегодня» показывал вчерашний день; помогал
 * только хард-ресет = пересчёт «today»). Датируя ключ фактической датой, на новый день
 * ключ ДРУГОЙ → cache miss → свежий fetch; вчерашний снимок не подставляется. ЕДИНЫЙ
 * источник ключа для экрана (`leaderboard-screen`) И прогрева (`leaderboard-prefetch`) —
 * не должны расходиться (иначе дедуп/снимок не совпадут). Префикс `daily:` отделяет
 * daily-страницы от all-time-ключей режимов (которые = просто `GameMode`).
 */
export function dailyPaneKey(dateOfDay: string): string {
  return `daily:${dateOfDay}`;
}

/** Это ключ daily-страницы (любой даты)? Для prune устаревших daily-снимков на rollover. */
export function isDailyPaneKey(key: string): boolean {
  return key.startsWith('daily:');
}

// =============================================================================
// Public API — load / upsert одной страницы / clear
// =============================================================================

/**
 * Прочитать снимок ТЕКУЩЕГО игрока СИНХРОННО (для мгновенного рендера при mount).
 * `null` — гость / нет снимка / битый / нет storage. Чистый read.
 */
export function loadLeaderboardSnapshot(): LeaderboardSnapshot | null {
  return store.load();
}

/**
 * Обновить (upsert) кэш ОДНОЙ страницы `key` свежими строками server-truth и сохранить.
 * Сливает с уже сохранёнными страницами (переключение вкладок не теряет кэш других).
 * No-op для гостя (ствол не пишет без playerId-ключа). Глубоко копирует строки, чтобы
 * снимок не «поехал» за источником.
 *
 * @param key — `PaneKey` страницы (вкладка борда / режим).
 * @param pane — свежая кэш-страница (score | karat).
 * @param now — ms epoch (инжектируемые часы; по умолчанию `Date.now()`).
 */
export function saveLeaderboardPane(key: string, pane: CachedPane, now: number = Date.now()): void {
  const current = store.load();
  const panes: Record<string, CachedPane> = { ...(current?.panes ?? {}) };
  panes[key] = clonePane(pane);
  store.save({ panes, timestamp: now });
}

/** Достать кэшированную страницу `key` из снимка, либо null (нет в кэше). */
export function paneFromSnapshot(snapshot: LeaderboardSnapshot | null, key: string): CachedPane | null {
  if (!snapshot) return null;
  return snapshot.panes[key] ?? null;
}

/** Удалить снимок текущего игрока (явный сброс / тесты). */
export function clearLeaderboardSnapshot(): void {
  store.clear();
}

/**
 * Выбросить из снимка устаревшие daily-страницы — все `daily:<date>`, чьи даты НЕ входят
 * в `keepDates` (сегодня/вчера). All-time-страницы (ключ = `GameMode`/`master_jeweler`)
 * не трогаем — они не датируются. Вызывается при детекте смены дня (rollover-fix 2026-06-11),
 * чтобы вчерашний (и старше) daily-снимок не залипал в localStorage. No-op для гостя.
 */
export function pruneStaleDailyPanes(keepDates: readonly string[]): void {
  const current = store.load();
  if (!current) return;
  const keep = new Set(keepDates.map((d) => dailyPaneKey(d)));
  const panes: Record<string, CachedPane> = {};
  let removed = false;
  for (const [key, pane] of Object.entries(current.panes)) {
    if (isDailyPaneKey(key) && !keep.has(key)) {
      removed = true;
      continue;
    }
    panes[key] = pane;
  }
  if (removed) store.save({ panes, timestamp: current.timestamp });
}

// =============================================================================
// Helpers
// =============================================================================

/** Глубокая копия страницы (строки readonly, но защищаемся от внешней мутации массива). */
function clonePane(pane: CachedPane): CachedPane {
  return pane.kind === 'score'
    ? { kind: 'score', rows: pane.rows.map((r) => ({ ...r })) }
    : { kind: 'karat', rows: pane.rows.map((r) => ({ ...r })) };
}

// =============================================================================
// Type guard
// =============================================================================

function isCachedRow(value: unknown): value is CachedRowVm {
  if (!value || typeof value !== 'object') return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v['rank'] === 'number' &&
    typeof v['playerName'] === 'string' &&
    typeof v['playerTag'] === 'string' &&
    (v['level'] === null || typeof v['level'] === 'number') &&
    typeof v['scoreVerified'] === 'number' &&
    typeof v['movesCount'] === 'number' &&
    typeof v['isViewer'] === 'boolean' &&
    (v['shortId'] === null || typeof v['shortId'] === 'string')
  );
}

function isCachedKaratRow(value: unknown): value is CachedKaratRowVm {
  if (!value || typeof value !== 'object') return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v['rank'] === 'number' &&
    typeof v['playerName'] === 'string' &&
    typeof v['playerTag'] === 'string' &&
    (v['level'] === null || typeof v['level'] === 'number') &&
    typeof v['karat'] === 'number' &&
    typeof v['isViewer'] === 'boolean'
  );
}

function isCachedPane(value: unknown): value is CachedPane {
  if (!value || typeof value !== 'object') return false;
  const v = value as Record<string, unknown>;
  if (!Array.isArray(v['rows'])) return false;
  if (v['kind'] === 'score') return (v['rows'] as unknown[]).every(isCachedRow);
  if (v['kind'] === 'karat') return (v['rows'] as unknown[]).every(isCachedKaratRow);
  return false;
}

function isLeaderboardSnapshot(value: unknown): value is LeaderboardSnapshot {
  if (!value || typeof value !== 'object') return false;
  const v = value as Record<string, unknown>;
  if (typeof v['timestamp'] !== 'number') return false;
  const panes = v['panes'];
  if (!panes || typeof panes !== 'object') return false;
  return Object.values(panes as Record<string, unknown>).every(isCachedPane);
}
